import 'material-components-web/dist/material-components-web.min.css';
import 'material-design-icons/iconfont/material-icons.css';

import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch, Action } from 'redux';
import { Rectangle, ComposerBridge } from 'ark-common';
import TabbedViewer from './TabbedViewer';
import { Publication } from '../../types/epub';
import { PagesState, TabType, makePopupPanelID } from '../../reducers/pages';
import HeaderToolbar from '../header-toolbar/HeaderToolbar';
import { AppStates, AppPictureStorage } from '../../reducers/app';
import {
  IntegratedDrawingState,
  collectPictureIDsFromJSONReadyIntegratedDrawingData, removeActiveLayer,
} from '../../reducers/integratedDrawing';
import { isReadyToSaveWorkspace } from '../../userdata/save';
import { PreferenceState } from '../../reducers/preference';
import * as TTSAction from '../../tts/actions/TTSAction';
import { SpeechState } from '../../tts/reducers/speech';
import {
  rangeToSpeechText,
  clearFeedbackClass,
  setSpeakingClass,
  setActiveClass,
  cancelIfSpeaking,
} from '../../tts/ttsutil';
import { UIModeState } from '../../reducers/uimode';
import { getActiveFrameId, getExtraNavItem, getActiveTabOrPopupId } from '../../helpers/pages';
import { ToolbarDirection, setToolbarDirection } from '../../actions/UIModeAction';
import { UIMode } from '../../../../../common/src/ts/types/UIMode';
import { SpeechText } from '../../tts/SpeechText';
import {
  getAncestorElements,
  getSelectedRangeOrAll,
  enterInnerContentIfNeeded,
  getContentDocument,
} from '../../../../../common/src/ts/utils/dom';
import { getGlobalFrameStore } from '../../services/FrameStore';
import { FrameInfo } from '../../../../../common/src/ts/types/FrameInfo';
import { getEditorKeyAndPage, getMultiroleLinks } from '../../helpers/integratedDrawings';
import { DrawingToolState } from '../../drawing/reducers/drawingTool';
import { UserData } from '../../userdata/types';
import { AutoSavingState, isAutoSavingRunning } from '../../reducers/autoSaving';
import { ContentStorageState } from '../../reducers/contentStorage';
import * as AppAction from '../../actions/AppAction';
import { ContentToViewer } from '../../../../../common/src/ts/types/messages';
import * as CFI from '../../../../../common/src/ts/cfi';
import { parseAll } from '../../../../../common/src/ts/parser';
import { PictureStorageState } from '../../drawing/reducers/pictureStorage';
import PictureStorageWorker from '../../drawing/helpers/PictureStorageWorker';
import createBlobFromCanvas from '../../drawing/util/createBlobFromCanvas';
import createCanvasFromBlob from '../../drawing/util/createCanvasFromBlob';
import PictureStorageAction from '../../drawing/actions/PictureStorageAction';
import PageNavigator from '../page-navigator/PageNavigator';
import NewPageNavigator from '../page-navigator/NewPageNavigator';
import { SelectionState, SelectionType } from '../../reducers/selection';
import { setSelectionState } from '../../actions/SelectionAction';
import NewHeaderToolbar from '../header-toolbar/NewHeaderToolbar';
import BottomToolbar from '../bottom-toolbar/BottomToolbar';
import { Tabbar } from '../tabbar/Tabbar';
import { NewTabbar } from '../tabbar/NewTabbar';
import { selectTab, closeTab, closePopup, openPopup } from '../../actions/PageAction';
import { discardHistory } from '../../actions/IntegratedDrawingAction';
import { zIndex } from '../../constants';
import SettingDialogContainer, { DialogType } from '../common/SettingDialogContainer';
import { TableOfContents } from '../toc/TableOfContents';
import HeaderToolbarPositionChangeButton from '../header-toolbar/HeaderToolbarPositionChangeButton';
import { setPan, setPopupRect, ensureState } from '../../actions/ViewAction';
import { ViewState } from '../../reducers/view';
import { Vector2D } from '../../types/common';
import Popup from './Popup';
import { createDefaultUIEventListeners } from '../../helpers/uievent';
import { MarkerInfo } from '../../marker';
import UIAction from '../../actions/ui/UIAction';
import { CurrentTool, UIState } from '../../reducers/ui/ui';
import { padZero } from '../../utils/string';
import ComposerContainer from '../../clipbook/ComposerContainer';
import { getSheetLayoutData } from '../../helpers/viewGlue';
import { isVerticalRightToLeftSheet } from '../../utils/view';

interface AppProps {
  publication: Publication | undefined;
  pageState: PagesState;
  viewState: ViewState;
  preferenceState: PreferenceState;
  drawingState: IntegratedDrawingState;
  drawingToolState: DrawingToolState;
  pictureStorage: PictureStorageState<HTMLCanvasElement>;
  uimodeState: UIModeState;
  dispatch: Dispatch<Action>;
  speechState: SpeechState;
  autoSaving: AutoSavingState;
  contentStorage: ContentStorageState;
  savingWorkspace: UserData | null;
  tabPanelAreaRect: Rectangle;
  selectionState: SelectionState;
  markers: MarkerInfo[];
  ui: UIState;
  composerMode: boolean;
  composerBridge: ComposerBridge;
  onOpenBookshelf: () => void;
  onOpenFileDialog: (cb: (filePath: string) => void) => void;
  onOpenFile: (filePath: string) => void;
  onReadAttachment: (id: string) => Promise<Blob>;
  onWriteAttachment: (id: string, blob: Blob) => Promise<void>;
  onSaveWorkspace: (data: UserData, attachments: ReadonlyArray<string>, filename: string) => void;
  onToggleFullScreen: () => void;
  // For UI Develop
  legacyUI: boolean;
}

const wrapperStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: '48px',
  right: 0,
  display: 'flex',
  flexGrow: 1,
  flexDirection: 'row',
};

interface AppState {
  dialogType: DialogType;
  showTableOfContents: boolean; // 新UI向け仮設
}

// TODO: Refactor
interface TTSContextProps {
  onPlay: () => any;
  onPause: () => any;
  onResume: () => any;
  onReset: () => any;
  onRateChange: (rate: number) => any;
}
// Fill later
export let TTSContext: React.Context<TTSContextProps>;

class App extends React.Component<AppProps, AppState> {

  private currentSpeechText?: SpeechText = undefined;
  private prevCurrentTool: CurrentTool = CurrentTool.Touch;

  private pictureStorageWorker = (
    new PictureStorageWorker<HTMLCanvasElement>({
      dispatch: results => this.props.dispatch(
        PictureStorageAction.finishTasks(AppPictureStorage.Drawing, results),
      ),
      read: id => this.props.onReadAttachment(id),
      write: (id, blob) => this.props.onWriteAttachment(id, blob),
      encode: image => createBlobFromCanvas(image),
      decode: blob => createCanvasFromBlob(document, blob),
    })
  );

  public constructor(props: AppProps) {
    super(props);
    this.updateActivePageId(props, true);

    const store = getGlobalFrameStore();
    store.addClickEventHandler(this.handleClick);
    store.addMessageHandler(
      ContentToViewer.MessageType.SelectionChange,
      this.handleSelectionChange,
    );

    this.state = { dialogType: DialogType.None, showTableOfContents: false };

    TTSContext = React.createContext({
      onPlay: this.handlePlay,
      onPause: this.handlePause,
      onResume: this.handleResume,
      onReset: this.handleReset,
      onRateChange: this.handleRateChange,
    });
  }

  public componentWillReceiveProps(nextProps: AppProps): void {
    this.updateActivePageId(nextProps);
  }

  private preventBrowserPinch(ev: TouchEvent): void {
    if (ev.touches.length > 1) {
      ev.preventDefault();
    }
  }

  public componentDidMount(): void {
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    document.addEventListener('touchstart', this.preventBrowserPinch, true);
    document.addEventListener('keydown', this.handleKeyDown, false);
  }

  public componentWillUnmount(): void {
    document.removeEventListener('touchstart', this.preventBrowserPinch);
    document.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  }

  public componentDidUpdate(prevProps: Readonly<AppProps>) {
    this.watchWorkspaceSaving(prevProps);
    this.pictureStorageWorker.update(prevProps.pictureStorage, this.props.pictureStorage);
  }

  private updateActivePageId(nextProps: AppProps, force: boolean = false): void {
    if (force || this.props.pageState !== nextProps.pageState) {
      const { currentSpeechText } = this;
      if (currentSpeechText !== undefined) {
        speechSynthesis.cancel();
        currentSpeechText.detach();
        this.currentSpeechText = undefined;
      }
    }
  }

  private watchWorkspaceSaving(prevProps: Readonly<AppProps>) {
    if (
      isReadyToSaveWorkspace(this.props.savingWorkspace, this.props.pictureStorage)
      && (
        prevProps.savingWorkspace !== this.props.savingWorkspace
        || !isReadyToSaveWorkspace(prevProps.savingWorkspace, prevProps.pictureStorage)
      )
    ) {
      this.saveWorkspace();
    }
  }

  private saveWorkspace() {
    const { savingWorkspace, publication } = this.props;
    if (savingWorkspace === null) {
      throw new Error('Illegal state.');
    }

    const attachments: { [id: string]: true } = {};

    for (const id of collectPictureIDsFromJSONReadyIntegratedDrawingData(
      savingWorkspace.integratedDrawings,
    )) {
      attachments[id] = true;
    }

    const date = new Date();
    const title = publication ? publication.renditions[0].title : '';
    const fileName = this.createFileNameForWorkspace(title, date);

    this.props.onSaveWorkspace(
      { ...savingWorkspace, createdAt: date },
      Object.keys(attachments),
      fileName,
    );
  }

  private createFileNameForWorkspace(textbookName: string, date: Date): string {
    const sanitizedName = this.sanitizeStringForFileName(textbookName);
    const title = sanitizedName.slice(0, 30);
    const dateString = [
      date.getFullYear(),
      padZero(date.getMonth() + 1, 2),
      padZero(date.getDate(), 2),
      '_',
      padZero(date.getHours(), 2),
      padZero(date.getMinutes(), 2),
      padZero(date.getSeconds(), 2),
    ].join('');
    const fileName = `${title}_${dateString}`;
    return fileName;
  }

  private sanitizeStringForFileName(str: string): string {
    const pattern = new RegExp('[\\\\\/:\?]', 'g');
    return str.replace(pattern, '_');
  }

  private handleSaveUserData = () => {
    if (this.props.savingWorkspace !== null) {
      // FIXME : This case should be prevented by forbidding operation during saving.
      return;
    }

    this.props.dispatch(AppAction.startSavingProcess());
  }

  private handleSendToClipbook = () => {
    global.arkSendSnapshotToClipbook(this.props.tabPanelAreaRect);
  }

  private async play(doc: Document, text: SpeechText, range: Range) {
    const { props, currentSpeechText } = this;
    await cancelIfSpeaking(currentSpeechText);
    setSpeakingClass(doc, range);
    text.rate = props.speechState.rate;
    text.onboundary = ({ currentNode, currentOffset = 0 }) => {
      if (currentNode !== undefined) {
        const nodes = getAncestorElements(currentNode);
        range.setEnd(currentNode, currentOffset);
        setActiveClass(doc, range, nodes);
        range.setStart(currentNode, currentOffset);

        this.scrollToTrackTTSPlaying(nodes, doc);
      }
    };
    text.onerror = text.onend = () => {
      this.props.dispatch(TTSAction.reset());
      clearFeedbackClass(doc);
      text.detach();
      this.currentSpeechText = undefined;
    };
    this.currentSpeechText = text;
    text.speak();
    this.props.dispatch(TTSAction.play());
  }

  private getOffsetFromRoot = (doc: Document): Vector2D => {
    const root = window.document;
    const offset = { x: 0, y: 0 };

    const frameElement = doc.defaultView && doc.defaultView.frameElement as HTMLFrameElement;
    if (frameElement === null) {
      return offset;
    }

    offset.x += frameElement.offsetLeft;
    offset.y += frameElement.offsetTop;

    // 紙面iframe(frameElement.ownerDocument === root)でなければさらに親を見る
    if (frameElement.ownerDocument && frameElement.ownerDocument !== root) {
      const ownerOffset = this.getOffsetFromRoot(frameElement.ownerDocument);
      offset.x += ownerOffset.x;
      offset.y += ownerOffset.y;
    }

    return offset;
  }

  private isPointerPressed(): boolean {
    const id = getActiveTabOrPopupId(this.props.pageState);
    const panel = this.props.viewState.tabStates[id];

    return panel.work != null && panel.work.type !== 'null';
  }

  private scrollToTrackTTSPlaying = (elements: Element[], doc: Document) => {
    if (!this.props.preferenceState.ttsTracking || this.isPointerPressed()) {
      return;
    }

    let element;

    // ルビ避け/Text要素(SVG向け)探し
    for (const elm of elements.slice().reverse()) {
      if (!/^r(uby|bc?|tc?|p)$/i.test(elm.nodeName)) {
        element = elm;
        break;
      }

      if (elm.nodeName.toLowerCase() === 'text') {
        element = elm;
        break;
      }
    }

    if (element === undefined) {
      return;
    }

    const { publication, pageState } = this.props;
    if (publication === undefined) {
      return;
    }

    // TODO: ポップアップの中
    const rect = element.getBoundingClientRect();
    const { tabs, activeTab } = pageState;
    const panelID = tabs[activeTab].id;

    const offset = this.getOffsetFromRoot(doc);

    // 読み上げ中ブロックの左上(右上)を縦中央(横中央)へ合わせる
    // 固定レイアウトの場合は basePosition が右ならRTLな縦書きコンテンツという想定にしている
    // ただし、仕様として記載されている訳ではないため、それ以外のコンテンツがくる可能性はある
    // また、LTRな縦書きコンテンツは対応しないためCenterと同じく下スクロールにしている
    // FIXME: 検討して決定した仕様ではない
    const tabState = this.props.viewState.tabStates[panelID];
    const data = getSheetLayoutData(publication, pageState, undefined, panelID);
    let x;
    let y;
    if (isVerticalRightToLeftSheet(data)) {
      const tabPanelAreaWidth = this.props.viewState.tabPanelAreaRect.width;
      x = -(rect.right + offset.x) * tabState.zoom + (tabPanelAreaWidth / 2);
      y = -(rect.top + offset.y) * tabState.zoom;
    } else {
      const tabPanelAreaHeight = this.props.viewState.tabPanelAreaRect.height;
      x = -(rect.left + offset.x) * tabState.zoom;
      y = -(rect.top + offset.y) * tabState.zoom + (tabPanelAreaHeight / 2);
    }

    this.props.dispatch(setPan(panelID, x, y));
  }

  private handlePlay = async () => {
    try {
      this.props.dispatch(ensureState(getActiveTabOrPopupId(this.props.pageState)));
    } catch (error) {
      // Log and rethrow error in promise
      console.log(error);
      throw error;
    }

    const activeFrameId = getActiveFrameId(this.props.pageState);
    const activeFrame = getGlobalFrameStore().getRef(activeFrameId);
    if (activeFrame === undefined) {
      return;
    }

    const doc = await enterInnerContentIfNeeded(await getContentDocument(activeFrame));
    const range = getSelectedRangeOrAll(doc);
    const text = await rangeToSpeechText(range);
    if (text !== undefined) {
      await this.play(doc, text, range);
    }
  }

  private handlePause = () => {
    if (speechSynthesis.speaking) {
      speechSynthesis.pause();
      this.props.dispatch(TTSAction.pause());
    } else {
      this.props.dispatch(TTSAction.reset());
    }
  }

  private handleResume = () => {
    if (speechSynthesis.speaking) {
      // suspendを実行するタイミングによって、再生が始まらないバグがあるため、
      // 回避策としてresumeを2回実行する
      // https://bugs.chromium.org/p/chromium/issues/detail?id=591191
      speechSynthesis.resume();
      speechSynthesis.resume();
      this.props.dispatch(TTSAction.resume());
    } else {
      this.props.dispatch(TTSAction.reset());
    }
  }

  private handleReset = () => {
    if (speechSynthesis.speaking) {
      speechSynthesis.cancel();
    } else {
      this.props.dispatch(TTSAction.reset());
    }
  }

  private handleRateChange = (rate: number) => {
    this.props.dispatch(TTSAction.rateChange(rate));
  }

  private handleBeforeUnload = (ev: BeforeUnloadEvent) => {
    if (isAutoSavingRunning(this.props.autoSaving)) {
      ev.preventDefault();
    }
  }

  private handleKeyDown = (ev: KeyboardEvent) => {
    switch (ev.key) {
      case 'Delete':
      case 'Backspace': {
        if (
          this.props.uimodeState.mode === UIMode.Drawing
          && document.activeElement !== null
          && document.activeElement.tagName.toLowerCase() !== 'input'
        ) {
          const { pageState, drawingState, publication } = this.props;
          if (publication === undefined) {
            throw new Error('Unexpected state.');
          }

          const tab = pageState.tabs[pageState.activeTab];
          if (tab.type === TabType.LinearPages) {
            const action = removeActiveLayer(
              drawingState, getEditorKeyAndPage(pageState, publication),
            );
            if (action !== null) {
              this.props.dispatch(action);
            }
          }
        }
        break;
      }
      default:
        break;
    }
  }

  private shouldPerformClickToTTSPlaying(): boolean {
    if (!this.props.uimodeState.ttsMode) {
      return false;
    }

    const id = getActiveTabOrPopupId(this.props.pageState);
    const panel = this.props.viewState.tabStates[id];
    if (panel == null) {
      return true;
    }

    const gestureStartTime = panel.gestureStartTimestamp;
    if (panel.work.type !== 'null' || gestureStartTime === null) {
      return false;
    }

    return Date.now() - gestureStartTime <= 300;
  }

  private handleClick = async (_info: FrameInfo, e: MouseEvent) => {
    if (!this.shouldPerformClickToTTSPlaying()) {
      return;
    }
    const node = e.target as Node;
    const doc = node.ownerDocument;
    if (doc === null) {
      return;
    }
    const range = doc.createRange();
    range.setEnd(doc, doc.childNodes.length);
    range.setStart(node, 0);
    const text = await rangeToSpeechText(range);
    if (text !== undefined) {
      await this.play(doc, text, range);
    }
  }

  private handleSelectionChange = (
    info: FrameInfo,
    { data: { cfi, text } }: { data: ContentToViewer.SelectionMessage },
  ) => {
    if (cfi !== undefined) {
      const parent = parseAll(info.reference[1], CFI.localPath);
      if (parent === undefined) {
        return;
      }
      const child = parseAll(cfi, CFI.cfiFragment);
      if (child === undefined) {
        return;
      }
      this.props.dispatch(setSelectionState({
        type: SelectionType.RANGE,
        cfi: `${info.reference[0]}#${child.prependParent(parent).toFragment()}`,
        text: text!,
      }));
    } else {
      this.props.dispatch(setSelectionState({ type: SelectionType.NONE }));
    }
  }

  private createHeaderToolbar = () => {
    const { publication } = this.props;
    if (publication === undefined) {
      throw new Error('Unexpected state.');
    }

    if (!this.props.legacyUI) {
      const value = {
        onPlay: this.handlePlay,
        onPause: this.handlePause,
        onResume: this.handleResume,
        onReset: this.handleReset,
        onRateChange: this.handleRateChange,
      };

      return (
        <TTSContext.Provider value={value}>
          <NewHeaderToolbar key="header" setDialogType={this.setDialogType} />
        </TTSContext.Provider>
      );
    }

    return <HeaderToolbar key="header"
      publication={publication}
      onPlay={this.handlePlay}
      onPause={this.handlePause}
      onResume={this.handleResume}
      onReset={this.handleReset}
      onSendToClipbook={this.handleSendToClipbook}
      onOpenFileDialog={this.props.onOpenFileDialog} />;
  }

  private createTabbedViewer = () => {
    return <TabbedViewer
      key="viewer"
      onBackToBookshelf={this.props.onOpenBookshelf}
      onSaveWorkspace={this.handleSaveUserData}
      onOpenFile={this.props.onOpenFile}
      drawing={this.props.uimodeState.mode === UIMode.Drawing}
    />;
  }

  private createSavingIndicator = (): JSX.Element | null => {
    if (!isAutoSavingRunning(this.props.autoSaving)) {
      return null;
    }

    const style: React.CSSProperties = {
      position: 'fixed',
      top: '0px',
      left: '50%',
      right: '50%',
      margin: '0 -100px 0 -100px',
      background: 'lightgray',
      textAlign: 'center',
      zIndex: zIndex.savingIndicator,
    };

    return (
      <div key="saving" style={style}>保存中...</div>
    );
  }

  private handleSelectTab = (index: number) => {
    this.props.dispatch(selectTab(index));
  }

  private closeTab(tabID: string) {
    this.props.dispatch(closeTab(tabID));
    this.props.dispatch(discardHistory(tabID));
  }

  private handleCloseTab = (index: number) => {
    // Temporary workaround
    if (index === 0) {
      return;
    }
    this.closeTab(this.props.pageState.tabs[index].id);
  }

  private createBottomToolbar = (): JSX.Element | null => {
    const tabbar = !this.props.legacyUI
      ? <NewTabbar
          tabs={this.props.pageState.tabs}
          activeTabIndex={this.props.pageState.activeTab}
          onSelect={this.handleSelectTab}
          onCloseButtonClick={this.handleCloseTab} />
      : <Tabbar
          tabs={this.props.pageState.tabs}
          activeTabIndex={this.props.pageState.activeTab}
          onSelect={this.handleSelectTab}
          onCloseButtonClick={this.handleCloseTab} />;

    return (
      <BottomToolbar
        onBackToBookshelf={this.props.onOpenBookshelf}
        onSaveWorkspace={this.handleSaveUserData}
        onToggleFullScreen={this.props.onToggleFullScreen}
        multiroleLinks={getMultiroleLinks(this.props.drawingState)}>
        {tabbar}
      </BottomToolbar>
    );
  }

  // 新UI向け仮設
  private createTableOfContents = (): JSX.Element | null => {
    const { publication, dispatch, pageState } = this.props;
    if (publication === undefined) {
      return null;
    }

    return (
      <TableOfContents
        isOpen={this.state.showTableOfContents}
        publication={publication}
        dispatch={dispatch}
        targetTab={pageState.activeTab}
        onClose={() => this.setState({ showTableOfContents: false })} />
    );
  }

  private handleTableOfContentsButtonClick = () => {
    const { pageState, publication, dispatch } = this.props;
    if (publication === undefined) {
      return;
    }

    const item = getExtraNavItem(pageState, publication);
    if (item !== null) {
      const { path, title } = item;
      const parent = pageState.tabs[pageState.activeTab].id;
      const [id, zIndex] = makePopupPanelID(pageState, parent, item.path);

      // 今のツールを覚えておいてズーム/パン/紙面ツールに持ち替え
      this.prevCurrentTool = this.props.ui.currentTool;
      UIAction.setCurrentTool(CurrentTool.Touch, dispatch);

      try {
        // 独自目次をモーダル設定決め打ちで表示
        dispatch(openPopup({
          id,
          zIndex,
          path,
          title,

          parent: null,
          modal: true,
          resizable: true,
          titlebar: true,
          frame: true,
          collapsed: false,

          topLevel: true,
          collapsible: false,
          needsRestoreCurrentTool: true,
          initialMargin: 40,

          // 指定が無ければ画面全体に目次が広がるようにする
          maxWidth: item.maxWidth || Number.MAX_VALUE,
          maxHeight: item.maxWidth || Number.MAX_VALUE,
        }));
      } catch (error) {
        alert(error.message);
      }

      return;
    }

    this.setState({ showTableOfContents: true });
  }

  private createHeaderToolbarRotationButton = (): JSX.Element => {
    const { dispatch } = this.props;
    const { VerticalLeft, Horizontal, VerticalRight } = ToolbarDirection;
    const { toolbarDirection } = this.props.uimodeState;

    const baseStyle: React.CSSProperties = {
      width: '30px',
      height: '90px',
      background: 'transparent',
    };

    const buttonStyle: React.CSSProperties = {
      position: 'absolute',
      bottom: '80px',
    };

    switch (toolbarDirection) {
      case VerticalLeft:
      case Horizontal: {
        return (
          <HeaderToolbarPositionChangeButton
            toolbarDirection={toolbarDirection}
            style={{ ...baseStyle, ...buttonStyle, right: 0 }}
            svgStyle={baseStyle}
            onClick={() => setToolbarDirection(VerticalRight, dispatch)} />
        );
      }
      case VerticalRight: {
        return (
          <HeaderToolbarPositionChangeButton
            toolbarDirection={toolbarDirection}
            style={{ ...baseStyle, ...buttonStyle, left: 0 }}
            svgStyle={baseStyle}
            onClick={() => setToolbarDirection(VerticalLeft, dispatch)} />
        );
      }
    }
  }

  public render() {
    const { VerticalLeft, Horizontal, VerticalRight } = ToolbarDirection;
    const { toolbarDirection } = this.props.uimodeState;
    const flexDirection = ((): React.CSSProperties => {
      switch (toolbarDirection) {
        case VerticalLeft:
          return { flexDirection: 'row' };
        case Horizontal:
          return { flexDirection: 'column' };
        case VerticalRight:
          return { flexDirection: 'row-reverse' };
      }
    })();

    wrapperStyle.bottom = !this.props.legacyUI ? '50px' : '48px';

    if (this.props.composerMode) {
      // XXX: ビューアをvisibility: hiddenで生かしておいたほうが良いかも
      return <ComposerContainer bridge={this.props.composerBridge} />;
    }

    return <>
      <div key="viewer" style={{ ...wrapperStyle, ...flexDirection }}>
        {this.createHeaderToolbar()}
        {this.createTabbedViewer()}
        <SettingDialogContainer
          dialogType={this.state.dialogType}
          setDialogType={this.setDialogType} />
      </div>
      {!this.props.legacyUI
        ? <NewPageNavigator
            onTableOfContentsButtonClick={this.handleTableOfContentsButtonClick} />
        : <PageNavigator />}
      {this.createHeaderToolbarRotationButton()}
      {this.createBottomToolbar()}
      {this.createTableOfContents()}
      {this.generateTopLevelPopups()}
      {this.createSavingIndicator()}
    </>;
  }

  // ツールバーより上の層、タブ領域の外に表示するポップアップを生成
  private generateTopLevelPopups(): JSX.Element {
    const { popups } = this.props.pageState;
    const modelessPopups: JSX.Element[] = [];
    const modalPopups: JSX.Element[] = [];

    // XXX: トップレベルポップアップが複数あった場合は未確認
    for (const id in popups) {
      const popup = popups[id];
      if (!popup.topLevel) {
        continue;
      }

      const handleChangeBounds = (rect: Rectangle) => {
        this.props.dispatch(setPopupRect(id, rect));
      };

      // TODO: UIEventListenersを使い分けて連続マーカーに対応させる
      // * 連続マーカーに対応させる時はFrameStore#frameFromPoint()をポップアップ対応に改修する必要あり
      const popupElement = (
        <Popup
          key={id}
          popupID={id}
          focus={true} // 実際にはフォーカスされない
          content={popup}
          uimode={this.props.uimodeState}
          uiEventListeners={createDefaultUIEventListeners(this.props.dispatch, id)}
          onChangeBounds={handleChangeBounds}
          onCloseButtonClick={() => this.props.dispatch(closePopup(id))}
          onClose={(popupInfo) => {
            if (popupInfo.needsRestoreCurrentTool) {
              UIAction.setCurrentTool(this.prevCurrentTool, this.props.dispatch);
            }
          }}
          onOpenFile={this.props.onOpenFile}
          markers={this.props.markers} />
      );

      if (popup.modal) {
        modalPopups.push(popupElement);
      } else {
        modelessPopups.push(popupElement);
      }
    }

    const modelessPopupContainerStyle: React.CSSProperties = {
      position: 'relative',
      zIndex: zIndex.modelessPopupContainer,
    };

    return <>
      <div style={modelessPopupContainerStyle}>{modelessPopups}</div>
      {modalPopups}
    </>;
  }

  private setDialogType = (dialogType: DialogType) => {
    this.setState({ dialogType });
  }

  public shouldComponentUpdate(nextProps: Readonly<AppProps>, nextState: Readonly<AppState>) {
    return (
      this.props.publication !== nextProps.publication
      || this.props.pageState !== nextProps.pageState
      || this.props.preferenceState !== nextProps.preferenceState
      || this.props.drawingState !== nextProps.drawingState
      || this.props.drawingToolState !== nextProps.drawingToolState
      || this.props.pictureStorage !== nextProps.pictureStorage
      || this.props.uimodeState !== nextProps.uimodeState
      || this.props.autoSaving !== nextProps.autoSaving
      || this.props.selectionState !== nextProps.selectionState
      || this.props.savingWorkspace !== nextProps.savingWorkspace
      || this.props.markers !== nextProps.markers
      || this.props.composerMode !== nextProps.composerMode
      || this.props.legacyUI !== nextProps.legacyUI
      || this.state.dialogType !== nextState.dialogType
      || this.state.showTableOfContents !== nextState.showTableOfContents
    );
  }
}

const mapStateToProps = (state: AppStates) => ({
  publication: state.publication,
  pageState: state.pages,
  viewState: state.view,
  preferenceState: state.preference,
  drawingState: state.integratedDrawing,
  drawingToolState: state.drawingTool,
  pictureStorage: state.pictureStorage,
  speechState: state.speech,
  uimodeState: state.uimode,
  autoSaving: state.autoSaving,
  contentStorage: state.contentStorage,
  savingWorkspace: state.savingWorkspace,
  tabPanelAreaRect: state.view.tabPanelAreaRect,
  selectionState: state.selectionState,
  markers: state.markers,
  ui: state.ui,
  composerMode: state.composerMode,
  legacyUI: state.legacyUI,
});

export default connect(mapStateToProps)(App);
